import React from 'react';
import { useTranslation } from 'react-i18next';

import { PublicAPI } from '../../api/public';
import { normalizeMediaUrl } from '../../api/base';
import { useCurrencyStore } from '../../store/useCurrencyStore';
import { useAppStore } from '../../store/useAppStore';

function mapHeroToSlide(hero) {
  const mainImg = normalizeMediaUrl(
    hero?.image ||  // Добавляем поддержку прямого поля image
    hero?.images?.find(x => x?.is_main)?.url ||
    hero?.images?.[0]?.url ||
    hero?.cover_url ||
    hero?.cover_thumb ||
    ''
  ) || null;

  // Безопасное извлечение названий
  const regionName = hero?.region?.name || hero?.region?.name_ru || hero?.region?.name_en || '';
  const districtName = hero?.district?.name || hero?.district?.name_ru || hero?.district?.name_en || '';
  const propertyTypeName = hero?.property_type?.name || hero?.property_type?.name_ru || hero?.property_type?.name_en || '';

  const parts = [
    regionName,
    districtName,
    propertyTypeName,
    typeof hero?.area_total === 'number' ? `${hero.area_total} м²` : null,
    typeof hero?.bedrooms === 'number' ? `${hero?.bedrooms} спальни` : null,
  ].filter(Boolean);

  return {
    id: hero?.id,
    slug: hero?.slug,
    title: String(hero?.title || 'Объект'),
    benefits: String(hero?.benefits || parts.join(' • ')), // Используем готовое поле benefits, если есть
    image: mainImg,
    region_id: hero?.region_id || hero?.region?.id || null,
    region: hero?.region || null,
  };
}

const hasPropImg = (x) => !!normalizeMediaUrl(
  x?.cover_url ||
  x?.cover_thumb ||
  x?.image ||
  x?.img ||
  x?.images?.find?.(m => m?.is_main)?.url ||
  x?.images?.[0]?.url ||
  ''
);

const hasCollectionCover = (c) => !!normalizeMediaUrl(
  c?.cover || c?.cover_thumb || c?.image ||
  (Array.isArray(c?.items) && c.items[0]?.cover_url) ||
  ''
);

const DEAL_TYPE_FOR_OBJECTS = '';

export function useHomeData() {
  const { t, i18n } = useTranslation();
  const currencyCode = useCurrencyStore((s) => s.currency) || 'AED';
  const regionId = useAppStore((s) => s.regionId);
  const regionSlug = useAppStore((s) => s.regionSlug);

  const [heroSlides, setHeroSlides] = React.useState([]);
  const [sections, setSections] = React.useState(null);
  const [awards, setAwards] = React.useState([]);
  const [recommended, setRecommended] = React.useState([]);
  const [objectsItems, setObjectsItems] = React.useState([]);
  const [objectsLabel, setObjectsLabel] = React.useState('');
  const [investItems, setInvestItems] = React.useState([]);
  const [districtsData, setDistrictsData] = React.useState([]);
  const [collections, setCollections] = React.useState([]);
  const [pdfCatalogs, setPdfCatalogs] = React.useState([]);
  const [media, setMedia] = React.useState([]);
  const [news, setNews] = React.useState([]);
  const [blog, setBlog] = React.useState([]);
  const [partners, setPartners] = React.useState([]);
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await PublicAPI.homeHero({
          locale: i18n.language || 'ru',
          currency: currencyCode || 'AED',
          region: regionSlug || undefined,
        });
        const items = Array.isArray(data?.items)
          ? data.items
          : (data?.hero ? [data.hero] : []);
        const slides = items.map(mapHeroToSlide);
        if (!cancelled) setHeroSlides(slides);
      } catch (_e) {
        if (!cancelled) setHeroSlides([]);
      }
    })();
    return () => { cancelled = true; };
  }, [i18n.language, currencyCode, regionSlug]);

  React.useEffect(() => {
    let cancelled = false;
    async function loadDistricts() {
      try {
        let regId = regionId;
        if (!regId && regionSlug) {
          try {
            const r = await PublicAPI.regions({ locale: i18n.language || 'ru' });
            const list = Array.isArray(r?.regions) ? r.regions : Array.isArray(r) ? r : [];
            const found = list.find((x) => String(x.slug || '') === String(regionSlug));
            regId = found?.id;
          } catch (_err) {
            /* noop */
          }
        }
        const params = { 
          locale: i18n.language || 'ru', 
          ...(regId ? { region_id: regId } : {}),
          _t: Date.now() // Добавляем timestamp для обхода кеша
        };
        const res = await PublicAPI.districts(params);
        const all = Array.isArray(res?.districts) ? res.districts : Array.isArray(res) ? res : [];
        if (!cancelled) setDistrictsData(all);
      } catch (_e) {
        if (!cancelled) setDistrictsData([]);
      }
    }
    loadDistricts();
    return () => { cancelled = true; };
  }, [i18n.language, regionSlug, regionId]);

  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      setErr('');
      try {
        const data = await PublicAPI.homeSections({
          locale: i18n.language || 'ru',
          currency: currencyCode || 'AED',
          region: regionSlug || undefined,
        });
        if (!cancelled) {
          setSections(data || {});
          setMedia(Array.isArray(data?.media) ? data.media : []);
          setNews(Array.isArray(data?.news) ? data.news : []);
          setBlog(Array.isArray(data?.blog) ? data.blog : []);
        }
      } catch (e) {
        if (!cancelled) setErr(e.message || 'Error');
      }
    }
    load();
    return () => { cancelled = true; };
  }, [i18n.language, currencyCode, regionSlug]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await PublicAPI.featuredProperties({
          locale: i18n.language || 'ru',
          currency: currencyCode || 'AED',
          limit: 12,
          region: regionSlug || undefined,
        });
        const list = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
        if (!cancelled) setRecommended(list);
      } catch (_e) {
        if (!cancelled) setRecommended([]);
      }
    })();
    return () => { cancelled = true; };
  }, [i18n.language, currencyCode, regionSlug]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = {
          locale: i18n.language || 'ru',
          currency: currencyCode || 'AED',
          page: 1,
          page_size: 12,
          ...(DEAL_TYPE_FOR_OBJECTS ? { deal_type: DEAL_TYPE_FOR_OBJECTS } : {}),
          region: regionSlug || undefined,
        };
        const res = await PublicAPI.properties(params);
        if (cancelled) return;
        const list = Array.isArray(res?.properties) ? res.properties
          : Array.isArray(res?.items) ? res.items
          : [];
        setObjectsItems(list);
        const defaultLabel = t('home.listings', 'Объекты');
        if (DEAL_TYPE_FOR_OBJECTS) {
          const dt = res?.applied_filters?.deal_type || res?.deal_type || {};
          const label =
            (i18n.language === 'en'
              ? (dt.name_en || dt.name || dt.name_ru)
              : (dt.name_ru || dt.name || dt.name_en)) || defaultLabel;
          setObjectsLabel(label);
        } else {
          setObjectsLabel(defaultLabel);
        }
      } catch (_e) {
        if (!cancelled) {
          setObjectsItems([]);
          setObjectsLabel(t('home.listings', 'Объекты'));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [i18n.language, currencyCode, regionSlug, t]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await PublicAPI.investmentProperties({
          locale: i18n.language || 'ru',
          currency: currencyCode || 'AED',
          limit: 12,
          region: regionSlug || undefined,
        });
        const list = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
        if (!cancelled) setInvestItems(list);
      } catch (_e) {
        if (!cancelled) setInvestItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [i18n.language, currencyCode, regionSlug]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await PublicAPI.collections({
          locale: i18n.language || 'ru',
          include: 'items',
          items_limit: 12,
          currency: currencyCode || 'AED',
          region: regionSlug || undefined,
        });
        const list = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
        if (!cancelled) setCollections(list);
      } catch (_e) {
        if (!cancelled) setCollections([]);
      }
    })();
    return () => { cancelled = true; };
  }, [i18n.language, currencyCode, regionSlug]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await PublicAPI.catalogs({
          locale: i18n.language || 'ru',
          page: 1,
          page_size: 6,
          region: regionSlug || undefined,
        });
        const list = Array.isArray(res?.items) ? res.items : (Array.isArray(res?.catalogs) ? res.catalogs : []);
        const mapped = list.map((c) => ({
          id: c.id,
          title: c.title || c.name || c.title_ru || c.title_en || 'Каталог',
          img: normalizeMediaUrl(c.cover || c.cover_thumb || c.image || ''),
          href: c.file_url || c.file || c.pdf || c.href || '#',
          subtitle: c.subtitle || c.description || '',
        }));
        if (!cancelled) setPdfCatalogs(mapped);
      } catch (_e) {
        if (!cancelled) setPdfCatalogs([]);
      }
    })();
    return () => { cancelled = true; };
  }, [i18n.language, regionSlug]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await PublicAPI.awards({
          locale: i18n.language || 'ru',
          page_size: 100,
        });
        const raw = res?.items || res?.awards || [];
        const list = raw.map((a) => {
          const date = a.created_at ? new Date(a.created_at) : null;
          return {
            id: a.id,
            label: a.title,
            title: a.title,
            subtitle: a.description
              || (a.year ? String(a.year) : '')
              || (date ? date.toLocaleDateString('ru-RU') : ''),
            description: a.description,
            img: a.image_thumb || a.image || a?.featured_media?.thumbnail_url || a?.featured_media?.url || null,
            href: `/awards/${a.id}`,
          };
        });
        if (!cancelled) setAwards(list);
      } catch (_e) {
        if (!cancelled) setAwards([]);
      }
    })();
    return () => { cancelled = true; };
  }, [i18n.language]);

  // Загрузка блога отдельно, если не приходит из homeSections
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await PublicAPI.blogPosts({
          locale: i18n.language || 'ru',
          page: 1,
          page_size: 6,
        });
        const list = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
        if (!cancelled && !blog.length) setBlog(list);
      } catch (_e) {
        if (!cancelled && !blog.length) setBlog([]);
      }
    })();
    return () => { cancelled = true; };
  }, [i18n.language, blog.length]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // TODO: Заменить на реальный API когда будет готов
        // const res = await PublicAPI.partners({ locale: i18n.language || 'ru', page_size: 8 });
        // const list = Array.isArray(res?.items) ? res.items : [];
        
        // Импортируем функцию для получения данных партнёров
        const { getPartnersData } = await import('../../data/partnersData');
        const mockPartners = getPartnersData(i18n.language || 'ru');
        
        if (!cancelled) setPartners(mockPartners);
      } catch (_e) {
        if (!cancelled) setPartners([]);
      }
    })();
    return () => { cancelled = true; };
  }, [i18n.language]);

  const districtsList = (districtsData && districtsData.length)
    ? districtsData
    : (sections?.districts ?? []);

  const recommendedList = (recommended && recommended.length)
    ? recommended
    : (sections?.recommended ?? sections?.recommend ?? []);

  const investFallback = sections?.invest ?? sections?.investment ?? [];
  const investList = (investItems && investItems.length) ? investItems : investFallback;

  const objectsFallback = sections?.objects ?? sections?.properties ?? [];
  const objectsList = (objectsItems && objectsItems.length) ? objectsItems : objectsFallback;
  const objectsTitle = objectsLabel || t('home.listings', 'Объекты');

  const collectionsList = (collections && collections.length) ? collections : (sections?.collections || []);
  const pdfList = (pdfCatalogs && pdfCatalogs.length) ? pdfCatalogs : (sections?.catalogs || []);

  const heroSlidesVisible = (heroSlides || []).filter(
    (s) => !!s.image && (!regionId || String(s?.region_id || s?.region?.id || '') === String(regionId)),
  );
  const recommendedVisible = (recommendedList || []).filter(
    (x) => hasPropImg(x) && (!regionId || String(x?.region_id || x?.region?.id || '') === String(regionId)),
  );
  const investVisible = (investList || []).filter(
    (x) => hasPropImg(x) && (!regionId || String(x?.region_id || x?.region?.id || '') === String(regionId)),
  );
  const districtsVisible = (districtsList || []).filter(
    (x) => (!regionId || String(x?.region_id || '') === String(regionId)),
  );
  const objectsVisible = (objectsList || []).filter(
    (x) => hasPropImg(x) && (!regionId || String(x?.region_id || x?.region?.id || '') === String(regionId)),
  );
  const collectionsVisible = (collectionsList || []).filter(hasCollectionCover);
  const pdfVisible = (pdfList || []).filter((x) => !!normalizeMediaUrl(x.img));
  const collectionsWithCoversOnly = (collections || []).filter(
    (c) => !!normalizeMediaUrl(c?.cover || c?.cover_thumb || c?.image || c?.cover_url || ''),
  );
  const awardsVisible = (awards || []).filter((x) => !!normalizeMediaUrl(
    x?.img || x?.image || x?.image_thumb || x?.featured_media?.thumbnail_url || x?.featured_media?.url || '',
  ));
  const mediaVisible = (media || []).filter((p) => !!normalizeMediaUrl(p?.cover || ''));
  const newsVisible = (news || []).filter((p) => !!normalizeMediaUrl(p?.cover || ''));
  const blogVisible = (blog || []).filter((p) => !!normalizeMediaUrl(p?.cover || ''));
  const partnersVisible = (partners || []).filter((p) => !!p.logo);


  const [winW, setWinW] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth : 1920,
  );
  React.useEffect(() => {
    const onR = () => setWinW(window.innerWidth);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);

  const perDesktop = (() => {
    // Логика: 2 элемента до 1024px, 4 элемента от 1024px
    return winW >= 1024 ? 4 : 2;
  })();
  
  const perDesktop3 = (() => {
    // Для блогов и медиа: 2 элемента до 1024px, 3 элемента от 1024px
    return winW >= 1024 ? 3 : 2;
  })();

  return {
    t,
    heroSlidesVisible,
    recommendedVisible,
    investVisible,
    districtsVisible,
    objectsVisible,
    objectsTitle,
    collectionsVisible,
    pdfVisible,
    collectionsWithCoversOnly,
    mediaVisible,
    newsVisible,
    blogVisible,
    partnersVisible,
    awardsVisible,
    perDesktop,
    perDesktop3,
    err,
  };
}

